import React, { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, MessageSquare, Search, Users } from "lucide-react";
import { AudienceFooter, AudienceHeader } from "./AudienceChrome";
import SEO from "./SEO";
import { useAuth } from "../context/AuthContext";
import "./NotFound.css";

/**
 * 404 page for any unmatched route.
 *
 * Before this existed the catch-all was simply absent, so an unknown URL
 * rendered the app shell with no <Routes> match — a blank page with only the
 * support widget floating on it, and no way back into the product.
 *
 * The destinations below are the real entry points to the app rather than a
 * generic "go home" button, because someone landing here from a stale link or
 * a typo usually wants a specific area, not the marketing page.
 */
export default function NotFound() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  const destinations = [
    {
      to: isAuthenticated ? "/research" : "/login",
      Icon: Search,
      title: "Research",
      body: "Analyse documents and synthesise case law.",
    },
    {
      to: "/debate-mode",
      Icon: MessageSquare,
      title: "Debate Mode",
      body: "Stress-test an argument across multiple AI models.",
    },
    {
      to: "/directory",
      Icon: Users,
      title: "Attorney Directory",
      body: "Browse and search the free attorney directory.",
    },
  ];

  return (
    <div className="nf-page">
      <SEO
        title="Page not found"
        path={pathname}
        description="The page you are looking for does not exist or has moved."
        noIndex
      />
      <AudienceHeader />

      <main className="nf-main">
        <div className="nf-inner">
          <p className="nf-code">404</p>
          <h1 className="nf-title">This page doesn&apos;t exist</h1>
          <p className="nf-lede">
            The link may be out of date, or the address may have a typo.
            Nothing is wrong with your account.
          </p>

          {/* Showing the attempted path makes a typo self-evident and gives
              support something concrete to work from. */}
          <p className="nf-path" title={pathname}>
            <span className="nf-path-label">You tried</span>
            <code>{pathname}</code>
          </p>

          <div className="nf-actions">
            <button
              type="button"
              className="nf-btn nf-btn-secondary"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft size={16} aria-hidden="true" />
              Go back
            </button>
            <Link to="/" className="nf-btn nf-btn-primary">
              Return home
            </Link>
          </div>

          <nav className="nf-links" aria-label="Popular destinations">
            {destinations.map(({ to, Icon, title, body }) => (
              <Link key={title} to={to} className="nf-link-card">
                <span className="nf-link-icon" aria-hidden="true">
                  <Icon size={18} />
                </span>
                <span className="nf-link-text">
                  <span className="nf-link-title">{title}</span>
                  <span className="nf-link-body">{body}</span>
                </span>
              </Link>
            ))}
          </nav>

          <p className="nf-support">
            Still stuck? <Link to="/faq">Read the FAQs</Link> or use the chat
            widget in the corner to reach support.
          </p>
        </div>
      </main>

      <AudienceFooter />
    </div>
  );
}
